import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TenantProvider, useTenant } from './contexts/TenantContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SuperAdminAuthProvider } from './contexts/SuperAdminAuthContext';
import './index.css';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import MyGamesPage from './pages/MyGamesPage';
import GameViewPage from './pages/GameViewPage';
import EmployeeLayout from './components/EmployeeLayout';
import AdminLayout from './components/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import ClaimValidation from './pages/admin/ClaimValidation';
import GameManagement from './pages/admin/GameManagement';
import CreateGame from './pages/admin/CreateGame';
import EditGame from './pages/admin/EditGame';
import EmployeeManagement from './pages/admin/EmployeeManagement';
import DepartmentManagement from './pages/admin/DepartmentManagement';
import ItemManagement from './pages/admin/ItemManagement';
import Settings from './pages/admin/Settings';
import GameDetails from './pages/admin/GameDetails';
import TipsManagement from './pages/admin/TipsManagement';
import LeaveManagement from './pages/admin/LeaveManagement';
import BrandingSettings from './pages/admin/BrandingSettings';
import SlackSettings from './pages/admin/SlackSettings';
import RevealPage from './pages/RevealPage';
import ProfilePage from './pages/ProfilePage';
import PendingApprovalPage from './pages/PendingApprovalPage';
import SignupPage from './pages/SignupPage';
import InvitationsPage from './pages/admin/InvitationsPage';
import { ToastProvider } from './components/Toast';
import { ThemeProvider } from './contexts/ThemeContext';
import './styles/theme.css';
import LeaveTracker from './pages/LeaveTracker';
import MyClaimsPage from './pages/MyClaimsPage';
import EmployeeClaimsPage from './pages/admin/EmployeeClaimsPage';

// Super Admin Pages
import SuperAdminLayout from './layouts/SuperAdminLayout';
import SuperAdminDashboard from './pages/super-admin/Dashboard';
import CompaniesList from './pages/super-admin/CompaniesList';
import CompanyDetail from './pages/super-admin/CompanyDetail';
import PlansPage from './pages/super-admin/Plans';
import ModulesPage from './pages/super-admin/Modules';
import AuditLogsPage from './pages/super-admin/AuditLogs';

// Contexts
import { SubscriptionProvider } from './contexts/SubscriptionContext';

const queryClient = new QueryClient();

// Landing Page (public)
import LandingPage from './pages/LandingPage';
import OnboardingWizard from './pages/OnboardingWizard';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';

function AppRoutes() {
    const { user, loading, isPending } = useAuth();

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-xl">Loading...</div>
            </div>
        );
    }

    // Show pending approval page for users awaiting admin approval
    if (isPending) {
        return (
            <Routes>
                <Route path="*" element={<PendingApprovalPage />} />
            </Routes>
        );
    }

    // Determine home redirect based on user role
    const getHomeRedirect = () => {
        if (!user) return <Navigate to="/login" />;
        if (user.role === 'super_admin') return <Navigate to="/super-admin" />;
        return <DashboardPage />;
    };

    return (
        <Routes>
            {/* Public landing page */}
            <Route path="/landing" element={<LandingPage />} />

            {/* Onboarding wizard - NOT for super admins */}
            <Route path="/onboarding" element={
                user
                    ? user.role === 'super_admin'
                        ? <Navigate to="/super-admin" />
                        : <OnboardingWizard />
                    : <Navigate to="/login" />
            } />

            <Route path="/login" element={!user ? <LoginPage /> : (user.role === 'super_admin' ? <Navigate to="/super-admin" /> : <Navigate to="/" />)} />
            <Route path="/signup" element={!user ? <SignupPage /> : <Navigate to="/" />} />

            {/* Employee routes with sidebar layout */}
            <Route element={user ? (user.role === 'super_admin' ? <Navigate to="/super-admin" /> : <EmployeeLayout />) : <Navigate to="/login" />}>
                <Route path="/" element={getHomeRedirect()} />
                <Route path="/games" element={<MyGamesPage />} />
                <Route path="/games/:gameId" element={<GameViewPage />} />
                <Route path="/games/:gameId/reveal" element={<RevealPage />} />
                <Route path="/leaves" element={<LeaveTracker />} />
                <Route path="/claims" element={<MyClaimsPage />} />
                <Route path="/profile" element={<ProfilePage />} />
            </Route>

            {/* Admin Routes */}
            <Route path="/admin" element={user ? <AdminLayout /> : <Navigate to="/login" />}>
                <Route index element={<AdminDashboard />} />
                <Route path="claims" element={<ClaimValidation />} />
                <Route path="games" element={<GameManagement />} />
                <Route path="games/new" element={<CreateGame />} />
                <Route path="games/:gameId" element={<GameDetails />} />
                <Route path="games/:gameId/edit" element={<EditGame />} />
                <Route path="employees" element={<EmployeeManagement />} />
                <Route path="employees/:id/claims" element={<EmployeeClaimsPage />} />
                <Route path="departments" element={<DepartmentManagement />} />
                <Route path="items" element={<ItemManagement />} />
                <Route path="tips" element={<TipsManagement />} />
                <Route path="leaves" element={<LeaveManagement />} />
                <Route path="invitations" element={<InvitationsPage />} />
                <Route path="branding" element={<BrandingSettings />} />
                <Route path="slack" element={<SlackSettings />} />
                <Route path="settings" element={<Settings />} />
            </Route>

            {/* Super Admin Routes */}
            <Route path="/super-admin" element={user?.role === 'super_admin' ? <SuperAdminLayout /> : <Navigate to="/" />}>
                <Route index element={<SuperAdminDashboard />} />
                <Route path="companies" element={<CompaniesList />} />
                <Route path="companies/:id" element={<CompanyDetail />} />
                <Route path="plans" element={<PlansPage />} />
                <Route path="modules" element={<ModulesPage />} />
                <Route path="audit-logs" element={<AuditLogsPage />} />
            </Route>
        </Routes>
    );
}

function WarpSpeedLoader() {
    return (
        <div className="fixed inset-0 bg-black flex items-center justify-center overflow-hidden">
            {/* Subtle star field */}
            <div className="absolute inset-0">
                {[...Array(30)].map((_, i) => (
                    <div
                        key={i}
                        className="absolute bg-white rounded-full opacity-70"
                        style={{
                            width: `${Math.random() * 2 + 1}px`,
                            height: `${Math.random() * 2 + 1}px`,
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                            animation: `twinkle ${Math.random() * 2 + 2}s ease-in-out infinite`,
                            animationDelay: `${Math.random() * 2}s`,
                        }}
                    />
                ))}
            </div>

            {/* Text */}
            <div className="relative z-10 text-center">
                <p className="text-white text-lg font-light tracking-wider opacity-90">
                    Entering interdimensional space...
                </p>
            </div>

            {/* CSS for animations */}
            <style>{`
                @keyframes twinkle {
                    0%, 100% { opacity: 0.3; }
                    50% { opacity: 1; }
                }
            `}</style>
        </div>
    );
}

function AppContent() {
    const { tenant, loading, error } = useTenant();


    if (loading) {
        return <WarpSpeedLoader />;
    }

    if (error || !tenant) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-red-600 mb-2">Tenant Not Found</h1>
                    <p className="text-gray-600">{error || 'Unable to identify your organization'}</p>
                </div>
            </div>
        );
    }

    const basename = tenant?.routingMode === 'path' ? `/${tenant.slug}` : '/';

    return (
        <BrowserRouter basename={basename}>
            <ThemeProvider>
                <ToastProvider>
                    <AuthProvider>
                        <SubscriptionProvider>
                            <AppRoutes />
                        </SubscriptionProvider>
                    </AuthProvider>
                </ToastProvider>
            </ThemeProvider>
        </BrowserRouter>
    );
}

function App() {
    // Check if we're on a public route that doesn't need tenant context
    const path = window.location.pathname;
    const isPublicRoute = path === '/landing' || path === '/' || path === '/terms' || path === '/privacy';
    const isSuperAdminRoute = path.startsWith('/super-admin');

    if (isPublicRoute) {
        return (
            <BrowserRouter>
                <Routes>
                    <Route path="/landing" element={<LandingPage />} />
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="/privacy" element={<PrivacyPage />} />
                </Routes>
            </BrowserRouter>
        );
    }

    // Super admin routes - no tenant context needed
    if (isSuperAdminRoute) {
        return (
            <QueryClientProvider client={queryClient}>
                <BrowserRouter>
                    <SuperAdminAuthProvider>
                        <Routes>
                            <Route path="/super-admin/*" element={<SuperAdminLayout />}>
                                <Route index element={<SuperAdminDashboard />} />
                                <Route path="companies" element={<CompaniesList />} />
                                <Route path="companies/:id" element={<CompanyDetail />} />
                                <Route path="plans" element={<PlansPage />} />
                                <Route path="modules" element={<ModulesPage />} />
                                <Route path="audit-logs" element={<AuditLogsPage />} />
                            </Route>
                        </Routes>
                    </SuperAdminAuthProvider>
                </BrowserRouter>
            </QueryClientProvider>
        );
    }

    return (
        <QueryClientProvider client={queryClient}>
            <TenantProvider>
                <AppContent />
            </TenantProvider>
        </QueryClientProvider>
    );
}

export default App;

